import {html, nothing, type TemplateResult} from 'lit';
import type {Character} from '../types';

interface Row {
  c: Character;
  label: string;
  /** 同じ説明が複数行に並ぶときは、いちばん重要な行にだけ残す */
  note: string | null;
}

/**
 * 「選んだ字 / 正解 / 基になる字（結合子音では構成要素）」の行を組み立てる。
 *
 * 表示と読み上げで同じ並びを使うので、ここに一本化してある。
 *
 * 同じ説明文が 2 行以上に並ぶことがある（स्त と त्य はどちらも「2 つの子音が
 * 1 つの形に組まれる」）。そのときは重要な行に残す。**表示順に先勝ちで落としてはいけない。**
 * 先頭は「選んだ字」＝間違いの行なので、素朴に先勝ちにすると
 * 「正解」や「基になる字」の説明が消えて、無関係な誤答の行にだけ文が残る。
 * 母音・子音・数字・ヴィラーマはカテゴリ内の note が全員同じなので、
 * 同一カテゴリ内で間違えると必ずそうなる。
 */
const NOTE_PRIORITY = ['正解', '基になる字', '構成要素', '選んだ字'];

export function feedbackRows(
  current: Character | null,
  bases: Character[],
  chosen: Character | null
): Row[] {
  if (!current) return [];

  const rows: Row[] = [];
  if (chosen && chosen.id !== current.id) {
    rows.push({c: chosen, label: '選んだ字', note: chosen.note ?? null});
  }
  rows.push({c: current, label: '正解', note: current.note ?? null});

  // 結合子音は基底が 2 つあるので「構成要素」と呼ぶ。1 つなら従来どおり「基になる字」
  const shown = bases.filter((b) => b.id !== current.id && b.id !== chosen?.id);
  for (const b of shown) {
    rows.push({c: b, label: shown.length > 1 ? '構成要素' : '基になる字', note: b.note ?? null});
  }

  const seen = new Set<string>();
  for (const row of [...rows].sort(
    (a, b) => NOTE_PRIORITY.indexOf(a.label) - NOTE_PRIORITY.indexOf(b.label)
  )) {
    if (!row.note) continue;
    if (seen.has(row.note)) row.note = null;
    else seen.add(row.note);
  }
  return rows;
}

/**
 * 不正解のときだけ出す対比パネル。
 *
 * 内在母音の概念は「記号がある形とない形を並べて」しか教えられないので、
 * 間違えた瞬間に基底文字（क = ka）と出題文字（क् = k）を並べて言葉で与える。
 * 結合子音では構成要素が 2 つ並ぶ（क्त なら क と त）。ただし क्ष / ज्ञ は
 * 構成要素の形が字に残らないので、QuizController 側が空の bases を返し、
 * note の「この形ごと覚える」だけが出る。
 *
 * 正解時は従来どおり ○ を出して 1.3 秒で自動送りするため、ここは描画されない。
 */
export function answerFeedback(
  current: Character | null,
  bases: Character[],
  chosen: Character | null,
  onContinue: () => void
): TemplateResult | typeof nothing {
  if (!current) return nothing;

  const line = ({c, label, note}: Row) => html`
    <p class="flex flex-wrap items-baseline gap-x-2">
      <span class="w-20 shrink-0 text-xs text-gray-600">${label}</span>
      <span lang="sa-Deva" class="font-devanagari text-2xl text-indigo-900">${c.char}</span>
      <span class="text-indigo-700">=</span>
      <span lang="sa-Latn" class="text-lg font-medium text-indigo-800">${c.romanized}</span>
      ${note ? html`<span class="text-sm text-gray-700">${note}</span>` : nothing}
    </p>
  `;

  return html`
    <div class="mt-8 rounded-xl bg-indigo-50 p-5">
      <div class="space-y-2">${feedbackRows(current, bases, chosen).map(line)}</div>
      <!-- 失敗を「回収できる出来事」として示す。箱が下がったことは言わない -->
      <p class="mt-3 text-xs text-gray-600">この字はまた出題されます</p>
      <div class="mt-4 text-center">
        <button
          type="button"
          part="continue"
          @click=${onContinue}
          class="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2"
        >
          次へ (Next)
        </button>
      </div>
    </div>
  `;
}

/**
 * スクリーンリーダー用に、対比の内容を 1 行のテキストにする。
 *
 * 説明は必ずその字の行に付けて読ませる。**末尾にまとめてはいけない。**
 * まとめると「正解 कः は kaḥ」の直後に誤答 कं の説明『アヌスヴァーラを添えた形』が
 * 読まれ、どの字の説明か分からないまま正解を取り違えさせる。
 * ヴィサルガとアヌスヴァーラの弁別はこのカテゴリの出題目的そのものなので、
 * 音声だけの利用者にとっては致命的になる。
 */
export function feedbackText(
  current: Character | null,
  bases: Character[],
  chosen: Character | null
): string {
  return feedbackRows(current, bases, chosen)
    .map((r) => `${r.label} ${r.c.char} は ${r.c.romanized}${r.note ? `、${r.note}` : ''}`)
    .join('。');
}
